import * as angular from 'angular';
import * as moment from 'moment';

import { ComponentDefinition } from '../../models';
import { ShewhartWidget, IshewhartResult } from './shewhart.widget';
import { ChartConfigurationOption } from '../../wtFramework';
import { IshewhartService, IDateRangeService, IwidgetIntervalHelperService, ILoggerService } from '../../services';
import { errorTextColor, infoTextColor, successShewhartColor, warningTextColor } from '../../common/constants';
import { refreshClick } from '../../common/events';
import { Helper } from '../../common/helpers';

class ShewhartController implements angular.IComponentController {
    item: ShewhartWidget;
    options: ChartConfigurationOption;
    series: any[];
    data: any[] = [];
    guid: string;
    isLoading: boolean;

    static $inject = [
        '$scope',
        '$translate',
        'shewhartService',
        'dateRangeService',
        'widgetIntervalHelperService',
        'loggerService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$translate: angular.translate.ITranslateService,
        private _shewhartService: IshewhartService,
        private _dateRangeService: IDateRangeService,
        private _widgetIntervalHelperService: IwidgetIntervalHelperService,
        private _loggerService: ILoggerService) {
    }

    $onInit() {
        this.initOptions();
        this.initSeries();
        this.guid = Helper.getGuid();
        this._$scope.$on(refreshClick, this.onRefresh());
        if (this._dateRangeService.initialized()) {
            this.refresh(this._dateRangeService.beginDate() as string, this._dateRangeService.endDate() as string);
        }
    }

    private initSeries() {
        const series: any[] = [];
        const coloringConditions = [
            {
                color: warningTextColor,
                condition: (d) => d.secondaryValue === 2
            },
            {
                color: errorTextColor,
                condition: (d) => d.secondaryValue === 1
            }
        ];
        const averageSeries = {
            identifier: 'shewhartMean',
            color: infoTextColor,
            'symbol': 'circle',
            isSymbolVisible: true,
            serieState: 0,
            isBarsVisible: false,
            isLineVisible: false,
            horizontalLines: [],
            pane: 0,
            selector: d => d.valueType,
            coloringConditions: coloringConditions,
            name: 'Mean',
            value: d => d.value
        };
        series.push(averageSeries);
        const averageStatusSeries = {
            identifier: 'shewhartMeanStatus',
            color: infoTextColor,
            isSymbolVisible: false,
            isBarsVisible: false,
            isLineVisible: false,
            horizontalLines: [],
            pane: 0,
            selector: d => d.valueType + 'Status',
            name: 'Status',
            value: d => this._$translate.instant(d.comment)
        };
        series.push(averageStatusSeries);
        const rangeSeries = {
            name: 'Range',
            color: infoTextColor,
            'symbol': 'circle',
            isSymbolVisible: true,
            serieState: 0,
            isBarsVisible: false,
            isLineVisible: false,
            horizontalLines: [],
            identifier: 'shewhartRange',
            selector: d => d.valueType,
            pane: 1,
            coloringConditions: coloringConditions,
            value: d => d.value
        };
        series.push(rangeSeries);
        const rangeStatusSeries = {
            identifier: 'shewhartRangeStatus',
            color: infoTextColor,
            isSymbolVisible: false,
            isBarsVisible: false,
            isLineVisible: false,
            horizontalLines: [],
            pane: 1,
            selector: d => d.valueType + 'Status',
            name: 'Status',
            value: d => this._$translate.instant(d.comment)
        };
        series.push(rangeStatusSeries);
        this.series = series;
    }

    private initOptions() {
        this.options = new ChartConfigurationOption();
        this.options.isTitleVisible = false;
        this.options.isLegendVisible = false;
        this.options.panes = [
            {
                height: 1,
                isXaxisVisible: false,
                isYaxisVisible: true,
                isHorizontalGridLinesVisible: false,
                isTitleLeftAligned: false,
                minHeight: null
            },
            {
                height: 1,
                isXaxisVisible: true,
                isYaxisVisible: true,
                isHorizontalGridLinesVisible: false,
                isTitleLeftAligned: false,
                minHeight: null
            }
        ];
    }

    private onRefresh() {
        return (event, data) => {
            const actualBeginDate = data.beginDate;
            const actualEndDate = data.endDate;
            this.refresh(actualBeginDate, actualEndDate);
        };
    }

    private refresh(beginDate: string, endDate: string) {
        this.isLoading = true;
        this.options.display.xMin = moment(beginDate).toDate();
        this.options.display.xMax = moment(endDate).toDate();
        const updatedInterval = this._widgetIntervalHelperService.getInterval(beginDate, endDate, {
            isEndDateIncluded: false,
            minPeriod: undefined,
            maxPeriod: undefined
        });
        beginDate = updatedInterval.beginMoment.toISOString();
        endDate = updatedInterval.endMoment.toISOString();
        this._shewhartService.getGetBetweenDates(this.item.configuration.guid, this.item.configuration.selectorType, beginDate, endDate)
            .then(result => {
                const shewhartResult: IshewhartResult = result[0];
                try {
                    if (shewhartResult) {
                        const averageLines = [
                            {
                                value: shewhartResult.meanAverage,
                                color: successShewhartColor
                            },
                            {
                                value: shewhartResult.meanLimits.lowerActionLimit,
                                color: errorTextColor
                            },
                            {
                                value: shewhartResult.meanLimits.upperActionLimit,
                                color: errorTextColor
                            },
                            {
                                value: shewhartResult.meanLimits.upperWarningLimit,
                                color: warningTextColor
                            },
                            {
                                value: shewhartResult.meanLimits.lowerWarningLimit,
                                color: warningTextColor
                            }
                        ];
                        this.options.panes[0].horizontalLines = averageLines;
                        const rangeLines = [
                            {
                                value: shewhartResult.rangeAverage,
                                color: successShewhartColor
                            },
                            {
                                value: shewhartResult.rangeLimits.lowerActionLimit,
                                color: errorTextColor
                            },
                            {
                                value: shewhartResult.rangeLimits.upperActionLimit,
                                color: errorTextColor
                            },
                            {
                                value: shewhartResult.rangeLimits.upperWarningLimit,
                                color: warningTextColor
                            },
                            {
                                value: shewhartResult.rangeLimits.lowerWarningLimit,
                                color: warningTextColor
                            }
                        ];
                        this.options.panes[1].horizontalLines = rangeLines;
                        this.data = shewhartResult.tagDateValues;
                    } else {
                        //default case, set everything to empty
                        this.options.panes[0].horizontalLines = [];
                        this.options.panes[1].horizontalLines = [];
                        this.data = [];
                    }
                } catch (e) {
                    this._loggerService.logError(e, true);
                }
                this.isLoading = false;
            })
            .catch(reason => this.isLoading = false);
    }
}

export const wtShewhart: ComponentDefinition = {
    componentId: 'wtShewhart',
    controller: ShewhartController,
    template: require('./shewhart.component.html'),
    controllerAs: 'vm',
    bindings: {
        item: '<'
    }
};
